import { createJsonMemoryAccessors } from '@/services/storage'

import {
  DEFAULT_CATEGORY_RATES,
  DEFAULT_GLOBAL_RATES,
  DEFAULT_PROFILES,
  mergeCategoryRates,
  ratesFromProfile,
} from './defaults'
import { PriceCalculator } from './PriceCalculator'
import { MarginCalculator } from './MarginCalculator'
import type {
  CategoryPricingConfig,
  FinancialLogEntry,
  GlobalPricingConfig,
  PriceHistoryEntry,
  PriceLineInput,
  PriceLineResult,
  PriceProfile,
  PriceSummary,
  PricingRates,
  RecalcTrigger,
} from './types'

const PROFILES_KEY = 'masterhub.price.profiles.v1'
const GLOBAL_KEY = 'masterhub.price.global.v1'
const CATEGORY_KEY = 'masterhub.price.categories.v1'
const HISTORY_KEY = 'masterhub.price.history.v1'
const LOGS_KEY = 'masterhub.price.logs.v1'
const ACTIVE_PROFILE_KEY = 'masterhub.price.activeProfile.v1'

type Memory = {
  profiles: PriceProfile[] | null
  global: GlobalPricingConfig | null
  categories: CategoryPricingConfig[] | null
  history: PriceHistoryEntry[] | null
  logs: FinancialLogEntry[] | null
  activeProfileId: string | null
}

const memory: Memory = {
  profiles: null,
  global: null,
  categories: null,
  history: null,
  logs: null,
  activeProfileId: null,
}

const { readJson, writeJson } = createJsonMemoryAccessors(memory)

/**
 * PriceEngine — facade for pricing calculations + config persistence.
 * Prepared for Snapshot Engine / Financial Panel / CRM integrations.
 */
export class PriceEngine {
  private readonly calculator = new PriceCalculator()
  private readonly margin = new MarginCalculator()

  ensureSeed(): void {
    const existingProfiles = readJson<PriceProfile[]>(PROFILES_KEY, 'profiles', [])
    if (existingProfiles.length === 0) {
      const now = new Date().toISOString()
      const profiles: PriceProfile[] = DEFAULT_PROFILES.map((p, i) => ({
        ...p,
        id: `price-profile-${i + 1}`,
        createdAt: now,
        updatedAt: now,
      }))
      writeJson(PROFILES_KEY, 'profiles', profiles)
      writeJson(ACTIVE_PROFILE_KEY, 'activeProfileId', profiles[0]!.id)
    }
    if (!readJson<GlobalPricingConfig | null>(GLOBAL_KEY, 'global', null)) {
      writeJson(GLOBAL_KEY, 'global', DEFAULT_GLOBAL_RATES)
    }
    const categories = readJson<CategoryPricingConfig[]>(CATEGORY_KEY, 'categories', [])
    if (categories.length === 0) {
      writeJson(CATEGORY_KEY, 'categories', DEFAULT_CATEGORY_RATES)
    }
  }

  listProfiles(): PriceProfile[] {
    this.ensureSeed()
    return readJson<PriceProfile[]>(PROFILES_KEY, 'profiles', [])
  }

  getActiveProfile(): PriceProfile {
    this.ensureSeed()
    const profiles = this.listProfiles()
    const activeId = readJson<string | null>(ACTIVE_PROFILE_KEY, 'activeProfileId', null)
    return profiles.find((p) => p.id === activeId) ?? profiles[0]!
  }

  setActiveProfile(id: string): void {
    const previous = activeIdSafe()
    writeJson(ACTIVE_PROFILE_KEY, 'activeProfileId', id)
    this.log('profile.activate', 'profile', id, previous, id)
  }

  updateProfile(id: string, patch: Partial<PriceProfile>): PriceProfile {
    const profiles = this.listProfiles()
    const index = profiles.findIndex((p) => p.id === id)
    if (index < 0) throw new Error('Perfil não encontrado')
    const previous = profiles[index]!
    const next: PriceProfile = {
      ...previous,
      ...patch,
      id,
      updatedAt: new Date().toISOString(),
    }
    profiles[index] = next
    writeJson(PROFILES_KEY, 'profiles', profiles)
    this.trackHistory('profile', id, 'rates', JSON.stringify(previous), JSON.stringify(next))
    this.log('profile.update', 'profile', id, JSON.stringify(previous), JSON.stringify(next))
    return next
  }

  getGlobalConfig(): GlobalPricingConfig {
    this.ensureSeed()
    return readJson(GLOBAL_KEY, 'global', DEFAULT_GLOBAL_RATES)
  }

  updateGlobalConfig(patch: Partial<GlobalPricingConfig>): GlobalPricingConfig {
    const previous = this.getGlobalConfig()
    const next = { ...previous, ...patch }
    writeJson(GLOBAL_KEY, 'global', next)
    this.trackHistory('config', 'global', 'rates', JSON.stringify(previous), JSON.stringify(next))
    this.log('config.global.update', 'config', 'global', JSON.stringify(previous), JSON.stringify(next))
    return next
  }

  listCategoryConfigs(): CategoryPricingConfig[] {
    this.ensureSeed()
    return readJson(CATEGORY_KEY, 'categories', [])
  }

  updateCategoryConfig(
    category: string,
    patch: Partial<Omit<CategoryPricingConfig, 'category'>>,
  ): CategoryPricingConfig {
    const list = this.listCategoryConfigs()
    const index = list.findIndex((c) => c.category === category)
    if (index < 0) throw new Error('Categoria não encontrada')
    const previous = list[index]!
    const next = { ...previous, ...patch }
    list[index] = next
    writeJson(CATEGORY_KEY, 'categories', list)
    this.trackHistory('config', category, 'categoryRates', JSON.stringify(previous), JSON.stringify(next))
    this.log('config.category.update', 'category', category, JSON.stringify(previous), JSON.stringify(next))
    return next
  }

  resolveRates(category?: string | null): PricingRates {
    const global = this.getGlobalConfig()
    const profile = this.getActiveProfile()
    const base = ratesFromProfile(profile, global)
    return mergeCategoryRates(base, category, this.listCategoryConfigs())
  }

  calculateLine(input: PriceLineInput, trigger: RecalcTrigger = 'components'): PriceLineResult {
    const rates = this.resolveRates(input.category)
    return this.calculator.recalculate(input, rates, trigger)
  }

  simulateProfitPercent(
    purchasePrice: number,
    quantity: number,
    profitPercent: number,
    category?: string | null,
  ): PriceLineResult {
    const rates = { ...this.resolveRates(category), profitPercent }
    return this.calculator.applyRates(purchasePrice, quantity, rates)
  }

  summarize(
    lines: PriceLineResult[],
    discountPercent = 0,
    discountValue = 0,
    freightAbsolute = 0,
  ): PriceSummary {
    const global = this.getGlobalConfig()
    return this.calculator.summarize(
      lines,
      discountPercent,
      discountValue,
      freightAbsolute,
      global.freightPercent,
    )
  }

  getAlerts(summary: PriceSummary) {
    return this.calculator.detectAlerts(summary)
  }

  getHealth(summary: PriceSummary) {
    return this.margin.health(
      summary.marginPercent,
      summary.profitNet,
      summary.finalPrice,
      summary.purchaseTotal,
    )
  }

  listHistory(): PriceHistoryEntry[] {
    return readJson<PriceHistoryEntry[]>(HISTORY_KEY, 'history', [])
  }

  listLogs(): FinancialLogEntry[] {
    return readJson<FinancialLogEntry[]>(LOGS_KEY, 'logs', [])
  }

  /** Architecture stubs for future modules */
  prepareSnapshotPayload(summary: PriceSummary) {
    return {
      engine: 'price-engine',
      version: 1,
      capturedAt: new Date().toISOString(),
      summary,
      profileId: this.getActiveProfile().id,
    }
  }

  resetMemory(): void {
    memory.profiles = null
    memory.global = null
    memory.categories = null
    memory.history = null
    memory.logs = null
    memory.activeProfileId = null
  }

  private trackHistory(
    entityType: PriceHistoryEntry['entityType'],
    entityId: string,
    field: string,
    previousValue: string | number,
    newValue: string | number,
  ): void {
    const entry: PriceHistoryEntry = {
      id: crypto.randomUUID(),
      entityType,
      entityId,
      field,
      previousValue,
      newValue,
      changedBy: 'admin@masterhub.com',
      changedAt: new Date().toISOString(),
    }
    const history = [entry, ...this.listHistory()].slice(0, 200)
    writeJson(HISTORY_KEY, 'history', history)
  }

  private log(
    action: string,
    entityType: string,
    entityId: string,
    previousValue: string,
    newValue: string,
  ): void {
    const entry: FinancialLogEntry = {
      id: crypto.randomUUID(),
      action,
      entityType,
      entityId,
      previousValue,
      newValue,
      changedBy: 'admin@masterhub.com',
      changedAt: new Date().toISOString(),
    }
    const logs = [entry, ...this.listLogs()].slice(0, 300)
    writeJson(LOGS_KEY, 'logs', logs)
  }
}

function activeIdSafe(): string {
  return readJson<string | null>(ACTIVE_PROFILE_KEY, 'activeProfileId', '') ?? ''
}

export const priceEngine = new PriceEngine()
